import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Hotel, X, Users, MapPin, Euro, Clock, Info, Calendar, Bed, TreePine, Tent, ChevronLeft, ChevronRight } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getRSVP, getConfig, createAccommodation } from '@/lib/api';

// Types de logements avec images et tarifs
const LODGING_TYPES = [
  {
    id: 'chambre',
    name: 'Chambre au domaine',
    description: 'Chambres confortables dans le domaine',
    price: 50,
    available: 10,
    image: '/uploads/chambre.png',
    icon: Bed
  },
  {
    id: 'cabane',
    name: 'Cabane dans les arbres',
    description: 'Expérience unique dans les arbres',
    price: 35,
    available: 1,
    image: '/uploads/cabane.png',
    icon: TreePine
  },
  {
    id: 'tente',
    name: 'Tente cloche confort',
    description: 'Tentes tout confort avec lit',
    price: 30,
    available: 4,
    image: '/uploads/tente-cloche.png',
    icon: Tent
  }
];

const AccommodationPage = () => {
  const { rsvpId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState(null);
  const [config, setConfig] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    accommodation_type: '',
    num_people: 1,
    comment: '',
    roommate_name: '',
    preferred_lodging: '',
    bring_own_tent: false,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rsvpRes, configRes] = await Promise.all([getRSVP(rsvpId), getConfig()]);
        setRsvp(rsvpRes.data);
        setConfig(configRes.data);
        
        // Set default num_people based on RSVP
        const totalPeople = (rsvpRes.data.adults?.length || 1) + (rsvpRes.data.children?.length || 0);
        setFormData((prev) => ({ ...prev, num_people: totalPeople }));
      } catch (error) {
        toast.error('Erreur lors du chargement');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [rsvpId, navigate]);

  const handleSubmit = async () => {
    if (!formData.accommodation_type) {
      toast.error('Veuillez sélectionner une option');
      return;
    }

    setSubmitting(true);
    try {
      await createAccommodation({
        rsvp_id: rsvpId,
        ...formData,
        room_type: formData.accommodation_type === 'domaine' ? 'domaine' : 'hotel',
        num_rooms: 1,
      });
      
      if (formData.accommodation_type === 'domaine') {
        toast.success('Votre demande d\'hébergement au domaine a été enregistrée ! Vous recevrez un email de confirmation.');
      } else if (formData.accommodation_type === 'hotel') {
        toast.success('Votre choix d\'hébergement à l\'hôtel a été noté.');
      }
      
      // Always go to confirmation - payment only after admin confirmation for domaine
      navigate(`/confirmation/${rsvpId}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const skipAccommodation = () => {
    navigate(`/confirmation/${rsvpId}`);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-stone">Chargement...</div>
        </div>
      </PublicLayout>
    );
  }

  const pricePerPerson = config?.price_per_person || 65;
  const estimatedTotal = formData.num_people * pricePerPerson;

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-terracotta-light/20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-4" data-testid="accommodation-title">
              Hébergement
            </h1>
            <p className="text-stone">
              Souhaitez-vous une solution d'hébergement pour le week-end du mariage ?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Options */}
            <RadioGroup
              value={formData.accommodation_type}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, accommodation_type: value }))}
              className="space-y-6"
            >
              {/* Option 1: Domaine */}
              <div
                data-testid="radio-domaine"
                className={`card-elegant cursor-pointer transition-all ${
                  formData.accommodation_type === 'domaine' ? 'ring-2 ring-terracotta' : 'hover:shadow-md'
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, accommodation_type: 'domaine' }))}
              >
                <div className="flex items-start gap-4">
                  <RadioGroupItem value="domaine" id="domaine" className="mt-1 text-terracotta" />
                  <div className="flex-1">
                    <Label htmlFor="domaine" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <Home className="w-6 h-6 text-terracotta" />
                        <span className="font-display text-xl text-charcoal">Hébergement au Domaine</span>
                      </div>
                      <p className="text-stone text-sm mb-4">
                        Domaine de la Rose Blanche - Ambiance conviviale garantie
                      </p>
                    </Label>
                    
                    {/* Ce que propose le domaine */}
                    <p className="text-sm font-semibold text-charcoal mb-3">Ce que propose le domaine :</p>
                    
                    <div className="bg-bone rounded-lg p-4 space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-terracotta" />
                        <span className="font-medium text-charcoal">Tarifs de 30 à 50 € / personne</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-terracotta" />
                        <span className="text-charcoal">Durée : 2 nuits (du samedi soir au lundi midi)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-terracotta" />
                        <span className="text-charcoal">Logements pour 2 personnes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bed className="w-4 h-4 text-terracotta" />
                        <span className="text-charcoal">Draps fournis</span>
                      </div>
                    </div>

                    {/* Types de logements avec images */}
                    <div className="mt-4 p-4 bg-white border border-stone-light rounded-lg">
                      <p className="text-sm font-medium text-charcoal mb-4">Types de logements disponibles :</p>
                      
                      {/* Carrousel d'images */}
                      <div className="relative mb-4">
                        <div className="overflow-hidden rounded-xl">
                          <div className="relative aspect-[4/3] bg-bone">
                            <img 
                              src={LODGING_TYPES[currentImageIndex].image} 
                              alt={LODGING_TYPES[currentImageIndex].name}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                              <div className="flex items-center gap-2 text-white">
                                {(() => {
                                  const IconComponent = LODGING_TYPES[currentImageIndex].icon;
                                  return <IconComponent className="w-5 h-5" />;
                                })()}
                                <span className="font-medium">{LODGING_TYPES[currentImageIndex].name}</span>
                              </div>
                              <p className="text-white/80 text-sm mt-1">
                                {LODGING_TYPES[currentImageIndex].price}€/personne • {LODGING_TYPES[currentImageIndex].available} disponible{LODGING_TYPES[currentImageIndex].available > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Navigation arrows */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + LODGING_TYPES.length) % LODGING_TYPES.length); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg"
                        >
                          <ChevronLeft className="w-5 h-5 text-charcoal" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % LODGING_TYPES.length); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg"
                        >
                          <ChevronRight className="w-5 h-5 text-charcoal" />
                        </button>
                        
                        {/* Dots */}
                        <div className="flex justify-center gap-2 mt-3">
                          {LODGING_TYPES.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                              className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-terracotta w-4' : 'bg-stone-light'}`}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {/* Menu déroulant pour choisir le logement */}
                      <div className="mt-4">
                        <Label className="text-sm font-medium text-charcoal mb-2 block">
                          Votre préférence de logement :
                        </Label>
                        <Select
                          value={formData.preferred_lodging}
                          onValueChange={(value) => setFormData((prev) => ({ ...prev, preferred_lodging: value }))}
                        >
                          <SelectTrigger 
                            data-testid="select-lodging"
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue placeholder="Choisir un type de logement..." />
                          </SelectTrigger>
                          <SelectContent>
                            {LODGING_TYPES.map((lodging) => (
                              <SelectItem key={lodging.id} value={lodging.id}>
                                <span className="flex items-center gap-2">
                                  <lodging.icon className="w-4 h-4" />
                                  {lodging.name} - {lodging.price}€/pers.
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-amber-600 mt-2 font-medium">
                          ⚠️ Sous réserve de disponibilité
                        </p>
                      </div>
                      
                      {/* Détails des tarifs */}
                      <div className="mt-4 pt-4 border-t border-stone-light">
                        <p className="text-sm text-charcoal mb-2">Récapitulatif des tarifs :</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-stone">• Chambres : <span className="text-charcoal font-medium">50 €/personne</span> pour le week-end</p>
                          <p className="text-stone">• Cabane : <span className="text-charcoal font-medium">35 €/personne</span> pour le week-end</p>
                          <p className="text-stone">• Tentes cloche : <span className="text-charcoal font-medium">30 €/personne</span> pour le week-end</p>
                        </div>
                      </div>
                      
                      {/* Enfants gratuits */}
                      <div className="mt-3 p-2 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-800 font-medium">Les enfants et adolescents ne paient pas.</p>
                      </div>
                      
                      {/* Option tente personnelle */}
                      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Tent className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-emerald-800 font-medium">
                              Option gratuite : Tente personnelle
                            </p>
                            <p className="text-xs text-emerald-700 mt-1">
                              Vous pouvez planter votre propre toile de tente dans le jardin du domaine, <strong>sans frais</strong>. 
                              <span className="text-emerald-600">(Maximum 8 emplacements disponibles)</span>
                            </p>
                            <label className="flex items-center gap-2 mt-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.bring_own_tent}
                                onChange={(e) => { e.stopPropagation(); setFormData((prev) => ({ ...prev, bring_own_tent: e.target.checked, preferred_lodging: e.target.checked ? 'tente_perso' : '' })); }}
                                className="w-4 h-4 text-emerald-600 rounded"
                              />
                              <span className="text-sm text-emerald-800">Je viendrai avec ma propre tente</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Note pour célibataires */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-800">
                          <strong>Pour les personnes seules / célibataires :</strong> Merci de nous indiquer le nom de la personne avec qui vous pourriez partager votre lit.
                        </p>
                      </div>
                      <Input
                        data-testid="input-roommate"
                        type="text"
                        placeholder="Nom de la personne avec qui partager..."
                        value={formData.roommate_name || ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, roommate_name: e.target.value }))}
                        className="mt-2 bg-white border-blue-200 focus:border-blue-400 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">
                          <strong>Important :</strong> La confirmation de la disponibilité vous sera envoyée par email. 
                          Le règlement sera demandé uniquement après confirmation de votre place.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Option 2: Hotel */}
              <div
                data-testid="radio-hotel"
                className={`card-elegant cursor-pointer transition-all ${
                  formData.accommodation_type === 'hotel' ? 'ring-2 ring-terracotta' : 'hover:shadow-md'
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, accommodation_type: 'hotel' }))}
              >
                <div className="flex items-start gap-4">
                  <RadioGroupItem value="hotel" id="hotel" className="mt-1 text-terracotta" />
                  <div className="flex-1">
                    <Label htmlFor="hotel" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-2">
                        <Hotel className="w-6 h-6 text-terracotta" />
                        <span className="font-display text-xl text-charcoal">Hôtel B&B</span>
                        <span className="text-xs bg-stone-light text-stone px-2 py-0.5 rounded-full">4 min (2,2 km)</span>
                      </div>
                      <p className="text-amber-600 text-sm font-medium mb-4">
                        Réservation à faire par vos soins
                      </p>
                    </Label>
                    
                    <div className="bg-bone rounded-lg p-4 space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-terracotta" />
                        <span className="text-charcoal">Environ <strong>60-80€</strong> / nuit</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-terracotta mt-0.5" />
                        <span className="text-charcoal">6 Parc Tertiaire Fenelon, 33370 Tresses</span>
                      </div>
                      <a href="https://www.hotel-bb.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                        <span className="w-4 h-4 text-terracotta">🌐</span>
                        hotel-bb.com
                      </a>
                      <a href="tel:0890649728" className="flex items-center gap-2 text-charcoal hover:text-terracotta">
                        <span className="w-4 h-4">📞</span>
                        0 890 64 97 28
                      </a>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4 text-terracotta border-terracotta hover:bg-terracotta hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://www.hotel-bb.com', '_blank');
                      }}
                    >
                      Réserver sur le site B&B
                    </Button>
                  </div>
                </div>
              </div>

              {/* Option 3: None */}
              <div
                data-testid="radio-none"
                className={`card-elegant cursor-pointer transition-all ${
                  formData.accommodation_type === 'none' ? 'ring-2 ring-terracotta' : 'hover:shadow-md'
                }`}
                onClick={() => setFormData((prev) => ({ ...prev, accommodation_type: 'none' }))}
              >
                <div className="flex items-start gap-4">
                  <RadioGroupItem value="none" id="none" className="mt-1 text-terracotta" />
                  <div className="flex-1">
                    <Label htmlFor="none" className="cursor-pointer">
                      <div className="flex items-center gap-2 mb-1">
                        <X className="w-5 h-5 text-stone" />
                        <span className="font-medium text-charcoal">Pas besoin d'hébergement</span>
                      </div>
                      <p className="text-stone text-sm">Je m'organise de mon côté</p>
                    </Label>
                  </div>
                </div>
              </div>
            </RadioGroup>

            {/* Details for domaine */}
            {formData.accommodation_type === 'domaine' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="card-elegant"
              >
                <h3 className="font-display text-lg text-charcoal mb-4">Détails de votre demande</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-charcoal font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-terracotta" />
                      Nombre de personnes à héberger
                    </Label>
                    <Input
                      data-testid="input-num-people"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.num_people}
                      onChange={(e) => setFormData((prev) => ({ ...prev, num_people: parseInt(e.target.value) || 1 }))}
                      className="input-elegant mt-2 w-32"
                    />
                  </div>

                  <div>
                    <Label className="text-charcoal font-medium">Commentaire (optionnel)</Label>
                    <Textarea
                      data-testid="input-comment"
                      value={formData.comment}
                      onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                      placeholder="Besoins particuliers, lit bébé, accessibilité..."
                      className="input-elegant mt-2 min-h-[80px]"
                    />
                  </div>

                  {/* Estimation tarif supprimée */}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
            <Button
              data-testid="btn-skip"
              type="button"
              variant="outline"
              onClick={skipAccommodation}
              className="btn-secondary"
            >
              Passer cette étape
            </Button>
            <Button
              data-testid="btn-submit-accommodation"
              type="button"
              onClick={handleSubmit}
              disabled={!formData.accommodation_type || submitting}
              className="btn-primary"
            >
              {submitting ? 'Envoi...' : 'Valider mon choix'}
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default AccommodationPage;
